import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  BASIS_LABELS,
  GATE_STATUS_LABELS,
  PURPOSE_LABELS,
  ROUTE_LABELS,
  STAGES,
  isFiscal,
  moduleEnabled,
  requiredReleaseRole,
  stageDef,
  type AuditEvent,
  type ModuleFlag,
  type Provenance,
  type ValidationItem,
  type ValidationResult,
} from '@ave/shared';
import { api, ApiError, type EngagementDetail } from '../lib/api';
import { dateTime, humaniseAction, longDate, money, moneyShort, pct, relativeTime } from '../lib/format';
import { useSession } from '../App';
import { Badge, Card, EmptyState, ErrorNotice, Field, ProvenanceChip, SectionHeading, Spinner } from '../components/ui';
import { Icon } from '../components/icons';
import { StageRail } from '../components/StageRail';
import { CalculationLog } from '../components/CalculationLog';
import { ReportPreview } from '../components/ReportPreview';
import { ExceptionsPanel } from '../components/ExceptionsPanel';
import { ValuationBridge, buildSegments } from '../components/ValuationBridge';
import { RecordHolding } from '../components/RecordHolding';
import { ValuationUnits } from '../components/ValuationUnits';
import { Instruments } from '../components/Instruments';
import { PensionReports } from '../components/PensionReports';
import { InstrumentClassifications } from '../components/InstrumentClassifications';
import { Interview } from '../components/Interview';
import { SumOfParts } from '../components/SumOfParts';
import { CapTable } from '../components/CapTable';
import { HeldReadings } from '../components/HeldReadings';
import { EmiPack } from '../components/EmiPack';
import { RecordAssumption } from '../components/RecordAssumption';
import { ChartBoundary } from '../components/ChartBoundary';
import {
  ComparableMultiplesChart,
  DiscountEvidenceChart,
  DiscountSensitivityHeatmap,
  EarningsWeightingChart,
  LatentTaxPositionChart,
  LatentTaxSensitivityChart,
  NavCompositionChart,
  ProvenanceSummaryChart,
  TransactionTimelineChart,
  type ChartData,
} from '../components/charts';

type Tab = 'workflow' | 'valuation' | 'captable' | 'report' | 'evidence' | 'audit';

/**
 * Where each kind of thing is shown, so a blocking check can take you to it.
 *
 * The map lives here rather than on the item because which tab shows a
 * methodology is a fact about this screen, not about the gate: the API is
 * entitled to say "this unit has no methodology" without knowing how the UI
 * is laid out.
 */
const TAB_FOR_ITEM: Record<ValidationItem['kind'], Tab> = {
  entity: 'evidence',
  holding: 'evidence',
  valuation_unit: 'evidence',
  methodology: 'evidence',
  finding: 'evidence',
  allotment_reading: 'evidence',
  external_valuation: 'evidence',
  exception: 'workflow',
  calc_run: 'valuation',
  report_section: 'report',
  action: 'workflow',
};

/**
 * The order the work happens in: evidence is assembled at Stage 5, the
 * calculation runs at Stage 6 and the report is drafted at Stage 8. Putting
 * the valuation first invites a reader to form a view of the number and then
 * go looking for evidence that supports it, which is the wrong way round for
 * a document that has to survive being argued with.
 */
const TABS: {
  id: Tab;
  label: string;
  icon: Parameters<typeof Icon>[0]['name'];
  /** Hidden where the licence says the tenant does not have this module. */
  module?: ModuleFlag;
}[] = [
  { id: 'workflow', label: 'Workflow', icon: 'list' },
  { id: 'evidence', label: 'Evidence', icon: 'book' },
  { id: 'valuation', label: 'Valuation', icon: 'calculator' },
  // Its own tab rather than a card under the instruments: a share class is a
  // different object from an instrument and answers a different question, and
  // the structure is too large to sit inside another card.
  { id: 'captable', label: 'Cap table', icon: 'users', module: 'instruments' },
  { id: 'report', label: 'Report', icon: 'document' },
  { id: 'audit', label: 'Audit trail', icon: 'archive' },
];

export function WorkspacePage() {
  const { id = '' } = useParams();
  const me = useSession();
  const [data, setData] = useState<EngagementDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('workflow');
  const [selectedStage, setSelectedStage] = useState<number | null>(null);
  /** The row a blocking check sent us to, so it can be scrolled to and flashed. */
  const [focusItemId, setFocusItemId] = useState<string | null>(null);
  /** Bumped to ask the holding form to open itself. */
  const [holdingFormNonce, setHoldingFormNonce] = useState(0);
  /** The same, for the valuation-unit form. */
  const [unitFormNonce, setUnitFormNonce] = useState(0);
  const [assumptionOpen, setAssumptionOpen] = useState(false);
  const [itemBusy, setItemBusy] = useState(false);
  const [itemError, setItemError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const detail = await api.getEngagement(id);
      setData(detail);
      setSelectedStage((s) => s ?? detail.engagement.currentStage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Scroll to whatever a blocking check sent us to, once the tab holding it
   * has rendered. Two frames of grace: the tab switch and the row's own
   * mount both have to land before the element exists to scroll to.
   */
  useEffect(() => {
    if (!focusItemId) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(`[data-item-id="${CSS.escape(focusItemId)}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('animate-flash-in');
      setTimeout(() => el.classList.remove('animate-flash-in'), 2600);
    }, 80);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [focusItemId, tab, data]);

  /**
   * What a blocking check's item does when it is clicked.
   *
   * An item that names a row goes to the row. An item that names a step
   * that has not been taken runs the step, where the step is something this
   * screen can do — and where it is not, it still moves to the tab that owns
   * the work rather than doing nothing, because a dead link on a blocking
   * check is worse than no link.
   */
  const openItem = useCallback(
    async (item: ValidationItem) => {
      setItemError(null);
      if (item.kind !== 'action') {
        setTab(TAB_FOR_ITEM[item.kind]);
        if (item.id) setFocusItemId(item.id);
        return;
      }
      const act = async (fn: () => Promise<unknown>) => {
        setItemBusy(true);
        try {
          await fn();
          await load();
        } catch (err) {
          setItemError(err instanceof Error ? err.message : 'That did not work');
        } finally {
          setItemBusy(false);
        }
      };
      switch (item.id) {
        case 'run_analysis':
          return act(() => api.runAnalysis(id));
        case 'draft_report':
          setTab('report');
          return act(() => api.draftReport(id));
        case 'record_holding':
        case 'record_entity':
          setTab('evidence');
          setHoldingFormNonce((n) => n + 1);
          return;
        case 'define_unit':
          setTab('evidence');
          setUnitFormNonce((n) => n + 1);
          return;
        case 'record_assumption':
          setAssumptionOpen(true);
          return;
        case 'release_report':
        case 'release_approval':
          setTab('report');
          return;
        default:
          // No handler: the step is real but this screen cannot yet take it.
          // Moving to the tab that owns the work is the honest response.
          setTab('evidence');
          return;
      }
    },
    [id, load],
  );

  if (error) return <ErrorNotice title="Could not load this engagement" message={error} />;
  if (!data) return <Spinner label="Loading engagement" />;

  const { engagement: e } = data;
  const stage = selectedStage ?? e.currentStage;
  const canReview = ['reviewer', 'owner', 'acumon_reviewer', 'acumon_admin'].includes(me.user.role);
  const canWrite = !me.user.licenceReadOnly && me.user.role !== 'viewer';
  const featureFlags = me.tenant.featureFlags ?? {};

  const outputsFor = (fn: string) =>
    [...data.runs].reverse().find((r) => r.functionName === fn && r.status === 'ok')?.outputs as
      | Record<string, string>
      | undefined;

  const segments = buildSegments({
    nav: outputsFor('nav_assembly'),
    capitalised: outputsFor('capitalised_earnings'),
    bridge: outputsFor('enterprise_to_equity'),
    holding: outputsFor('holding_value'),
  });

  const latestReport = data.reports[0] ?? null;
  const openExceptions = data.exceptions.filter((x) => x.status === 'pending');

  // Everything the exhibits plot. Facts carry their provenance so the
  // evidential-basis exhibit counts what the report actually rests on.
  const chartData: ChartData = {
    runs: data.runs,
    evidencePoints: data.evidencePoints,
    shareTransactions: data.shareTransactions,
    facts: [
      ...data.units.map((u) => ({ provenance: u.provenance as string })),
      ...data.findings.map((f) => ({ provenance: f.provenance })),
      ...data.shareTransactions.map((t) => ({ provenance: t.provenance })),
      ...data.externals.map(() => ({ provenance: 'external_expert' })),
    ],
    valuationDate: e.valuationDate,
    currency: e.currency,
  };

  return (
    <div className="space-y-6">
      {assumptionOpen ? (
        <RecordAssumption
          engagementId={e.id}
          valuationDate={e.valuationDate}
          onClose={() => setAssumptionOpen(false)}
          onSaved={load}
        />
      ) : null}

      <EngagementHeader detail={data} />

      <Card className="px-6 py-5">
        <StageRail
          gates={data.gates}
          currentStage={e.currentStage}
          selected={stage}
          onSelect={setSelectedStage}
        />
      </Card>

      <nav className="flex gap-1 border-b border-slate-200">
        {/* The API refuses an unlicensed module and says whose job the fix
            is, which is the control. This is the courtesy: a tab that leads
            to a 403 is worse than no tab, and a module the licence has not
            mentioned is shown, because not stated is not no. */}
        {TABS.filter((t) => t.module === undefined || moduleEnabled(featureFlags, t.module)).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'border-teal-600 text-teal-700'
                : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
            }`}
          >
            <Icon name={t.icon} className="h-4 w-4" />
            {t.label}
            {t.id === 'workflow' && openExceptions.length > 0 ? (
              <span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-bold text-white">
                {openExceptions.length}
              </span>
            ) : null}
          </button>
        ))}
      </nav>

      {tab === 'workflow' ? (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <StageDetail
              detail={data}
              stage={stage}
              canReview={canReview}
              canWrite={canWrite}
              onChange={load}
              onOpenItem={openItem}
              itemBusy={itemBusy}
              itemError={itemError}
            />
          </div>
          <div className="space-y-6">
            <ExceptionsPanel
              exceptions={data.exceptions}
              canApprove={
                canWrite &&
                (e.route === 'r3_client'
                  ? ['acumon_reviewer', 'acumon_admin'].includes(me.user.role)
                  : canReview)
              }
              onChange={load}
              footer={
                canWrite ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setAssumptionOpen(true)}
                  >
                    <Icon name="plus" className="h-4 w-4" strokeWidth={2} />
                    Key an assumption
                  </button>
                ) : null
              }
            />
            <RelianceRegister detail={data} />
          </div>
        </div>
      ) : null}

      {tab === 'valuation' ? (
        <div className="space-y-6">
          {segments.length > 0 ? (
            <Card>
              <SectionHeading
                title="Valuation bridge"
                subtitle="From the assets valued to the figure that goes on the return — every bar is a calculation step"
              />
              <div className="px-6 py-6">
                <ValuationBridge segments={segments} />
              </div>
            </Card>
          ) : null}

          {/* Above the calculation log and below the bridge: the sum of the
              parts is a valuation output, and it reads as one once the units
              beneath it have been valued. It draws nothing where none have. */}
          <SumOfParts engagementId={e.id} canWrite={canWrite} currency={e.currency} />

          {/* The VAL231 figures are valuation output, so they belong with the
              calculation rather than with the evidence — and above the log,
              because they are what an EMI engagement exists to produce. */}
          {e.purpose === 'emi_csop' ? (
            <EmiPack
              engagementId={e.id}
              details={data.emiDetails}
              run={
                [...data.runs]
                  .reverse()
                  .find((r) => r.functionName === 'emi_share_values' && r.status === 'ok') ?? null
              }
              canWrite={canWrite}
              onChange={load}
            />
          ) : null}

          <Exhibits data={data} chartData={chartData} />
          <CalculationLog runs={data.runs} />
        </div>
      ) : null}

      {tab === 'report' ? (
        <div className="space-y-6">
          <ReleasePanel
            detail={data}
            canReview={canReview}
            canWrite={canWrite}
            onChange={load}
          />
          <ReportPreview
            report={latestReport}
            engagement={e}
            chartData={chartData}
            entityName={data.entities[0]?.name ?? e.name}
          />
        </div>
      ) : null}

      {tab === 'evidence' ? (
        <EvidencePanel
          detail={data}
          canWrite={canWrite}
          canReview={canReview}
          onChange={load}
          openHoldingForm={holdingFormNonce}
          openUnitForm={unitFormNonce}
        />
      ) : null}
      {tab === 'captable' ? (
        <CapTable
          engagementId={e.id}
          entities={data.entities}
          canWrite={canWrite}
          currency={e.currency}
        />
      ) : null}
      {tab === 'audit' ? <AuditTrail engagementId={e.id} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------

function EngagementHeader({ detail }: { detail: EngagementDetail }) {
  const e = detail.engagement;
  const holdingOutputs = [...detail.runs]
    .reverse()
    .find((r) => r.functionName === 'holding_value' && r.status === 'ok')?.outputs as
    | Record<string, string>
    | undefined;
  const navOutputs = [...detail.runs]
    .reverse()
    .find((r) => r.functionName === 'nav_assembly' && r.status === 'ok')?.outputs as
    | Record<string, string>
    | undefined;

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-100 px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs font-medium text-slate-400">{e.ref}</span>
              <Badge tone={e.status === 'released' ? 'emerald' : 'slate'}>
                {e.status === 'released' ? 'Released' : 'Active'}
              </Badge>
              <Badge tone="slate" title={ROUTE_LABELS[e.route]}>
                {ROUTE_LABELS[e.route].split('—')[0]?.trim()}
              </Badge>
            </div>
            <h1 className="mt-1.5 text-xl font-semibold tracking-tight text-slate-900">{e.name}</h1>
            {e.clientRef ? (
              <p className="mt-0.5 text-sm text-slate-400">Client reference {e.clientRef}</p>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-6">
            {navOutputs ? (
              <Headline label="Company net asset value" value={moneyShort(navOutputs.net_asset_value, e.currency)} />
            ) : null}
            {holdingOutputs ? (
              <Headline
                label="Value of the holding"
                value={moneyShort(holdingOutputs.holding_value, e.currency)}
                emphasis
              />
            ) : null}
          </div>
        </div>
      </div>

      <dl className="grid gap-5 px-6 py-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Purpose">
          {PURPOSE_LABELS[e.purpose]}
          {isFiscal(e.purpose) ? (
            <span className="ml-2 text-xs text-slate-400">
              resolves to a value per holding
            </span>
          ) : null}
        </Field>
        <Field label="Basis of value">{BASIS_LABELS[e.basisOfValue]}</Field>
        <Field label="Valuation date">
          <span className="tnum">{longDate(e.valuationDate)}</span>
        </Field>
        <Field label="Standards corpus">
          <span className="tnum">as at {longDate(e.standardsAsof)}</span>
        </Field>
      </dl>

      {e.notes ? (
        <p className="border-t border-slate-100 px-6 py-3 text-sm leading-relaxed text-slate-600">
          {e.notes}
        </p>
      ) : null}
    </Card>
  );
}

function Headline({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="text-right">
      <p className="label">{label}</p>
      <p
        className={`tnum mt-0.5 font-semibold tracking-tight ${
          emphasis ? 'text-2xl text-teal-700' : 'text-2xl text-slate-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------

function StageDetail({
  detail,
  stage,
  canReview,
  canWrite,
  onChange,
  onOpenItem,
  itemBusy,
  itemError,
}: {
  detail: EngagementDetail;
  stage: number;
  canReview: boolean;
  canWrite: boolean;
  onChange: () => void;
  /** What to do when a preparer clicks one of the things a check named. */
  onOpenItem: (item: ValidationItem) => void;
  itemBusy: boolean;
  itemError: string | null;
}) {
  const def = stageDef(stage);
  const gate = detail.gates.find((g) => g.stage === stage);
  const e = detail.engagement;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validations = gate?.validations ?? [];
  const blocking = validations.filter((v) => v.severity === 'blocking' && !v.passed);
  const warnings = validations.filter((v) => v.severity === 'warning' && !v.passed);
  const reachable = stage <= e.currentStage;
  const needsReviewer = def.owner === 'reviewer' || stage === 9;
  const mayApprove = canWrite && reachable && (!needsReviewer || canReview);

  const act = async (decision: 'approve' | 'reject') => {
    setBusy(true);
    setError(null);
    try {
      if (decision === 'approve') await api.approveStage(e.id, stage, note || undefined);
      else await api.rejectStage(e.id, stage, note || 'Returned for rework');
      setNote('');
      onChange();
    } catch (err) {
      setError(
        err instanceof ApiError && err.error === 'gate_blocked'
          ? `${err.message}. Clear the blocking checks below and try again.`
          : err instanceof Error
            ? err.message
            : 'Failed',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <SectionHeading
        title={`Stage ${stage} — ${def.name}`}
        subtitle={def.summary}
        right={
          <Badge
            tone={
              gate?.status === 'passed'
                ? 'emerald'
                : gate?.status === 'blocked'
                  ? 'amber'
                  : gate?.status === 'rejected'
                    ? 'rose'
                    : 'slate'
            }
          >
            {GATE_STATUS_LABELS[gate?.status ?? 'not_started']}
          </Badge>
        }
      />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-slate-100 bg-slate-50/60 px-6 py-2.5 text-xs text-slate-500">
        <span>
          <span className="text-slate-400">Owner:</span> {def.ownerLabel}
        </span>
        <span>
          <span className="text-slate-400">Entry:</span> {def.entry.join(', ')}
        </span>
      </div>

      {!reachable ? (
        <EmptyState
          title="Not reached yet"
          detail={`This stage opens once stage ${e.currentStage} is complete. Its checks run live from that point.`}
        />
      ) : (
        <>
          <ul className="divide-y divide-slate-100">
            {validations.map((v) => (
              <ValidationRow
                key={v.id}
                validation={v}
                onOpenItem={onOpenItem}
                canWrite={canWrite && !itemBusy}
              />
            ))}
            {validations.length === 0 ? (
              <li className="px-6 py-5 text-sm text-slate-500">No automated checks at this stage.</li>
            ) : null}
          </ul>

          {gate?.status === 'passed' ? (
            <div className="border-t border-slate-100 bg-emerald-50/50 px-6 py-3">
              <p className="text-sm text-emerald-900">
                <Icon name="check" className="mr-1 inline h-4 w-4" strokeWidth={2.5} />
                Gate passed {gate.approvedAt ? dateTime(gate.approvedAt) : ''}
                {gate.note ? ` — ${gate.note}` : ''}
              </p>
            </div>
          ) : (
            <div className="border-t border-slate-100 px-6 py-4">
              {blocking.length > 0 ? (
                <div className="mb-3">
                  <p className="text-sm text-amber-800">
                    <Icon name="alert" className="mr-1 inline h-4 w-4" />
                    {blocking.length} blocking check{blocking.length === 1 ? '' : 's'} must pass
                    before this gate can open.
                    {blocking.some((v) => v.items?.length) ? (
                      <span className="text-amber-700"> Each one lists what is standing in the way.</span>
                    ) : null}
                  </p>
                  {itemError ? <p className="mt-1 text-sm text-rose-600">{itemError}</p> : null}
                </div>
              ) : (
                <p className="mb-3 text-sm text-slate-600">
                  All blocking checks pass
                  {warnings.length > 0
                    ? `; ${warnings.length} warning${warnings.length === 1 ? '' : 's'} will be recorded against your approval.`
                    : '. The gate is ready to close.'}
                </p>
              )}

              <textarea
                value={note}
                onChange={(ev) => setNote(ev.target.value)}
                rows={2}
                placeholder="Note recorded with your decision (optional on approval, required on rejection)"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  disabled={busy || blocking.length > 0 || !mayApprove}
                  onClick={() => act('approve')}
                >
                  <Icon name="check" className="h-4 w-4" strokeWidth={2.5} />
                  Pass this gate
                </button>
                {stage > 0 ? (
                  <button
                    type="button"
                    className="btn-danger"
                    disabled={busy || !canReview || !canWrite}
                    onClick={() => act('reject')}
                  >
                    Return for rework
                  </button>
                ) : null}
                {!mayApprove ? (
                  <span className="text-xs text-slate-500">
                    {!canWrite
                      ? 'Read-only session.'
                      : `This gate is closed by ${def.ownerLabel.toLowerCase()}.`}
                  </span>
                ) : null}
              </div>

              {error ? <p className="mt-2 text-sm text-rose-600">{error}</p> : null}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function ValidationRow({
  validation: v,
  onOpenItem,
  canWrite,
}: {
  validation: ValidationResult;
  onOpenItem: (item: ValidationItem) => void;
  canWrite: boolean;
}) {
  const tone = v.passed
    ? 'text-emerald-500'
    : v.severity === 'blocking'
      ? 'text-amber-500'
      : 'text-slate-300';
  return (
    <li className="px-6 py-3">
      <div className="flex items-start gap-3">
        <Icon
          name={v.passed ? 'checkCircle' : v.severity === 'blocking' ? 'alert' : 'clock'}
          className={`mt-0.5 h-[18px] w-[18px] shrink-0 ${tone}`}
        />
        <div className="min-w-0 flex-1">
          <p className={`text-sm ${v.passed ? 'text-slate-700' : 'font-medium text-slate-900'}`}>
            {v.label}
          </p>
          {v.detail ? <p className="mt-0.5 text-xs text-slate-500">{v.detail}</p> : null}
        </div>
        {!v.passed ? (
          <Badge tone={v.severity === 'blocking' ? 'amber' : 'slate'}>
            {v.severity === 'blocking' ? 'Blocking' : 'Warning'}
          </Badge>
        ) : null}
      </div>

      {/* The things the check is actually complaining about. A count tells a
          preparer the size of the problem and nothing about where it is;
          these take them to it. */}
      {!v.passed && v.items?.length ? (
        <ul className="mt-2 space-y-1 border-l-2 border-slate-100 pl-3 sm:ml-[30px]">
          {v.items.map((item, i) => (
            <li key={`${item.kind}:${item.id ?? i}`}>
              <button
                type="button"
                onClick={() => onOpenItem(item)}
                disabled={!canWrite}
                className="group flex w-full items-baseline gap-2 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700 group-hover:text-slate-900">
                  {item.label}
                </span>
                {item.remedy ? (
                  <span className="shrink-0 text-xs text-teal-700 group-hover:text-teal-900 group-disabled:text-slate-400">
                    {item.remedy}
                    {canWrite ? ' →' : ''}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

// ---------------------------------------------------------------------

function ReleasePanel({
  detail,
  canReview,
  canWrite,
  onChange,
}: {
  detail: EngagementDetail;
  canReview: boolean;
  canWrite: boolean;
  onChange: () => void;
}) {
  const e = detail.engagement;
  const me = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const report = detail.reports[0] ?? null;
  const needed = requiredReleaseRole(e.route);
  const mayDecide =
    canWrite &&
    (needed === 'acumon_reviewer'
      ? ['acumon_reviewer', 'acumon_admin'].includes(me.user.role)
      : canReview);

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await fn();
      setMessage(ok);
      onChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (e.status === 'released' && report?.status === 'released') {
    return (
      <Card className="border-emerald-200">
        <div className="flex items-start gap-3 px-6 py-4">
          <Icon name="archive" className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          <div>
            <p className="text-sm font-semibold text-emerald-900">Released and archived</p>
            <p className="mt-0.5 text-sm text-emerald-800">
              The defence file holds the report, the full calculation log, the evidence snapshot,
              the corpus version pins and the gate history — the record for an HMRC negotiation or a
              PI claim.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <SectionHeading
        title="Release gate"
        subtitle={
          e.route === 'r3_client'
            ? 'Direct-client engagements require an Acumon reviewer to approve before release. The client cannot self-approve.'
            : `Release requires approval from a ${needed}.`
        }
      />
      <div className="flex flex-wrap items-center gap-2 px-6 py-4">
        <button
          type="button"
          className="btn-secondary"
          disabled={busy || !canWrite}
          onClick={() => run(() => api.draftReport(e.id), 'Draft regenerated from the calculation log.')}
        >
          <Icon name="document" className="h-4 w-4" />
          {report ? 'Regenerate draft' : 'Generate draft'}
        </button>

        <button
          type="button"
          className="btn-secondary"
          disabled={busy || !mayDecide}
          onClick={() =>
            run(
              () =>
                api.releaseApproval(e.id, {
                  decision: 'approved',
                  note: 'Reviewed in full against the checklist.',
                  reportId: report?.id,
                }),
              'Review approval recorded.',
            )
          }
        >
          <Icon name="shield" className="h-4 w-4" />
          Record review approval
        </button>

        <button
          type="button"
          className="btn-primary"
          disabled={busy || !mayDecide || !report}
          onClick={() =>
            run(
              () => api.releaseReport(e.id, report!.id),
              'Released. The archive has been written.',
            )
          }
        >
          Release &amp; archive
        </button>
      </div>

      {message ? (
        <p className="border-t border-slate-100 px-6 py-3 text-sm text-emerald-700">{message}</p>
      ) : null}
      {error ? (
        <p className="border-t border-slate-100 px-6 py-3 text-sm text-rose-600">{error}</p>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------

function RelianceRegister({ detail }: { detail: EngagementDetail }) {
  const fiscal = isFiscal(detail.engagement.purpose);
  return (
    <Card>
      <SectionHeading
        title="Reliance register"
        subtitle="Property and actuarial values are external expert inputs. This engine never opines on them."
      />
      {detail.externals.length === 0 ? (
        <EmptyState title="No external valuations" detail="Nothing is being relied upon." />
      ) : (
        <ul className="divide-y divide-slate-100">
          {detail.externals.map((x) => {
            const scopeOk = x.relianceCoversClient && x.relianceCoversHmrc;
            return (
              <li key={x.id} data-item-id={x.id} className="scroll-mt-24 rounded-lg px-6 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{x.assetDescription}</p>
                    <p className="mt-0.5 text-sm text-slate-500">
                      {x.expertFirm}
                      {x.expertName ? ` · ${x.expertName}` : ''}
                    </p>
                  </div>
                  <span className="tnum shrink-0 text-sm font-semibold text-slate-900">
                    {money(x.value, x.currency)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <ProvenanceChip provenance={'external_expert' as Provenance} />
                  <Badge tone="slate">{x.designation === 'rics' ? 'RICS' : x.designation}</Badge>
                  {fiscal ? (
                    <Badge tone={scopeOk ? 'emerald' : 'rose'}>
                      {scopeOk ? 'Reliance covers client + HMRC' : 'Reliance scope insufficient'}
                    </Badge>
                  ) : null}
                </div>
                {x.basis ? <p className="mt-2 text-xs text-slate-500">{x.basis}</p> : null}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/**
 * The exhibits, in the workspace as well as the report.
 *
 * A reviewer works these before they read a word of narrative: the
 * sensitivity charts show which judgements the answer actually turns on,
 * which is where review time is worth spending.
 */
function Exhibits({ data, chartData }: { data: EngagementDetail; chartData: ChartData }) {
  const has = (fn: string) => data.runs.some((r) => r.functionName === fn && r.status === 'ok');

  const panels: { title: string; subtitle: string; node: ReactNode; wide?: boolean }[] = [];

  if (has('nav_assembly')) {
    panels.push({
      title: 'Gross asset composition',
      subtitle: 'What the company is made of, before liabilities and latent tax',
      node: <NavCompositionChart data={chartData} />,
      wide: true,
    });
  }
  if (has('maintainable_earnings')) {
    panels.push({
      title: 'Maintainable earnings',
      subtitle: 'Normalised earnings by period and the weighting applied',
      node: <EarningsWeightingChart data={chartData} />,
    });
  }
  if (has('capitalised_earnings')) {
    panels.push({
      title: 'Comparable multiples',
      subtitle: 'The multiple adopted against the comparable set',
      node: <ComparableMultiplesChart data={chartData} />,
    });
  }
  if (has('latent_tax')) {
    panels.push({
      title: 'Latent tax position',
      subtitle: 'The deduction adopted, and where it sits in the corridor',
      node: <LatentTaxPositionChart data={chartData} />,
    });
  }
  if (has('holding_sensitivity')) {
    panels.push({
      title: 'Sensitivity to the latent-tax position',
      subtitle: 'How much of the answer rests on this argument',
      node: <LatentTaxSensitivityChart data={chartData} />,
    });
    panels.push({
      title: 'Sensitivity to the discounts',
      subtitle: 'Holding value across both evidence ranges',
      node: <DiscountSensitivityHeatmap data={chartData} />,
      wide: true,
    });
  }
  if (has('holding_value') || has('discounts')) {
    panels.push({
      title: 'Discounts against the evidence',
      subtitle: 'The point adopted, the range it came from, and the published observations',
      node: <DiscountEvidenceChart data={chartData} />,
      wide: true,
    });
  }
  panels.push({
    title: 'Transactions in the subject shares',
    subtitle: 'Relative to the valuation date',
    node: <TransactionTimelineChart data={chartData} />,
  });
  panels.push({
    title: 'Evidential basis',
    subtitle: 'Provenance of the facts this valuation rests on',
    node: <ProvenanceSummaryChart data={chartData} />,
  });

  if (panels.length === 0) return null;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {panels.map((p) => (
        <Card key={p.title} className={p.wide ? 'lg:col-span-2' : ''}>
          <SectionHeading title={p.title} subtitle={p.subtitle} />
          <div className="px-6 py-5">
            <ChartBoundary label={p.title}>{p.node}</ChartBoundary>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ConsiderationBadge({ kind }: { kind: string | undefined }) {
  if (kind === 'cash') return null;
  return (
    <Badge tone="amber">
      {!kind || kind === 'unknown'
        ? 'consideration unstated'
        : `${kind.replace(/_/g, '-')} consideration`}
    </Badge>
  );
}

function EvidencePanel({
  detail,
  canWrite,
  canReview,
  onChange,
  openHoldingForm,
  openUnitForm,
}: {
  detail: EngagementDetail;
  canWrite: boolean;
  /** Methodology approval is a reviewer act; Stage 4 will not exit without it. */
  canReview: boolean;
  onChange: () => void | Promise<void>;
  /** Increments when a blocking check asks for the holding form. */
  openHoldingForm: number;
  /** Increments when a blocking check asks for the valuation-unit form. */
  openUnitForm: number;
}) {
  const e = detail.engagement;
  // Read here rather than threaded down from the page: the licence decides
  // whether a whole card exists, and a prop for it would have to be passed
  // through every panel that does not care.
  const featureFlags = useSession().tenant.featureFlags ?? {};
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <SectionHeading
          title="Holdings"
          subtitle={
            isFiscal(e.purpose)
              ? 'A fiscal valuation resolves to a value per holding at the top entity'
              : undefined
          }
        />
        <ul className="divide-y divide-slate-100">
          {detail.holdings.map((h) => (
            <li key={h.id} data-item-id={h.id} className="scroll-mt-24 rounded-lg px-6 py-4">
              <p className="text-sm text-slate-800">{h.holderDescription}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge tone="slate">{pct(h.pctHolding, 2)} of {h.shareClass}</Badge>
                <Badge tone={h.controlAssessment === 'control' ? 'emerald' : 'slate'}>
                  {h.controlAssessment.replace(/_/g, ' ')}
                </Badge>
                {h.aggregatedPct ? (
                  <Badge tone="violet" title="IHTA 1984 s.161 related property">
                    aggregates to {pct(h.aggregatedPct, 2)} (s.161)
                  </Badge>
                ) : null}
              </div>
              {h.relatedPropertyNotes ? (
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  {h.relatedPropertyNotes}
                </p>
              ) : null}
            </li>
          ))}
          {detail.holdings.length === 0 ? (
            <li>
              <EmptyState
                title="No holdings recorded"
                detail="Stage 0 does not close without one: a valuation with no holding has nothing to resolve to."
              />
            </li>
          ) : null}
        </ul>
        {canWrite ? (
          <RecordHolding
            engagementId={e.id}
            purpose={e.purpose}
            entities={detail.entities}
            onSaved={onChange}
            openSignal={openHoldingForm}
          />
        ) : null}
      </Card>

      <div className="space-y-6">
        <Card>
          <SectionHeading
            title="Transactions in the subject shares"
            subtitle="A recent arm’s-length transaction is near-determinative and forces anchor-or-justify"
          />
          {detail.shareTransactions.length === 0 ? (
            <EmptyState
              title="None found"
              detail="The hunt was performed across SH01 filings and client records. Finding none is a valid outcome; never having looked is not."
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {detail.shareTransactions.map((t) => (
                <li key={t.id} data-item-id={t.id} className="scroll-mt-24 rounded-lg px-6 py-3.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="tnum text-sm text-slate-800">{longDate(t.txnDate)}</span>
                    <span className="tnum text-sm font-semibold text-slate-900">
                      {money(t.pricePerShare)} / share
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <Badge tone={t.counterpartyRelationship === 'arms_length' ? 'emerald' : 'amber'}>
                      {t.counterpartyRelationship.replace(/_/g, ' ')}
                    </Badge>
                    {/* Cash is the unremarkable case and stays quiet. Anything
                        else is shown, because a price agreed inside an
                        arrangement is not a market price and a reader
                        scanning this list must not have to open the notes to
                        discover that.

                        The field is read defensively because the services
                        deploy serially: for the length of a promotion the web
                        is newer than the API, and a missing field must read
                        as "unstated" rather than blank the panel. */}
                    <ConsiderationBadge kind={t.considerationKind} />
                    <ProvenanceChip provenance={t.provenance as Provenance} />
                  </div>
                  {t.notes ? <p className="mt-1.5 text-xs text-slate-500">{t.notes}</p> : null}
                  {t.sourceReference ? (
                    <a
                      href={t.sourceReference}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1 inline-block text-xs text-sky-700 underline underline-offset-2 hover:text-sky-900"
                    >
                      The filed form
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Directly under the transactions, because that is what these are
            candidates for and what their absence from that list means. */}
        <HeldReadings
          engagementId={e.id}
          readings={detail.allotmentReadings ?? []}
          canWrite={canWrite}
          onChange={onChange}
        />

        <ValuationUnits
          engagementId={e.id}
          valuationDate={e.valuationDate}
          units={detail.units}
          entities={detail.entities}
          shareTransactions={detail.shareTransactions}
          canWrite={canWrite}
          canReview={canReview}
          onChange={onChange}
          openSignal={openUnitForm}
        />

        {/* Under the units, because an instrument is priced against a value
            the units produce and reads oddly above the thing it depends on. */}
        <Instruments
          engagementId={e.id}
          instruments={detail.instruments ?? []}
          entities={detail.entities}
          canWrite={canWrite}
          canReview={canReview}
          onChange={onChange}
        />

        {/* Under the library rather than above it, because a proposal is
            answered by comparing it with what is already recorded — and
            because the card is empty on most engagements, and an empty card
            above a full one reads as the more important of the two. */}
        <InstrumentClassifications
          engagementId={e.id}
          classifications={detail.instrumentClassifications ?? []}
          instruments={detail.instruments ?? []}
          entities={detail.entities}
          canWrite={canWrite}
          canReview={canReview}
          onChange={onChange}
        />

        {/* Last, and only where the licence grants it. A pension position is
            somebody else's completed work that this engagement relies on,
            rather than something produced here, so it reads after the things
            that are. */}
        {moduleEnabled(featureFlags, 'pensions') ? (
          <PensionReports engagementId={e.id} canWrite={canWrite} onChange={onChange} />
        ) : null}
      </div>
    </div>
  );
}

function AuditTrail({ engagementId }: { engagementId: string }) {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);

  useEffect(() => {
    api
      .audit(engagementId)
      .then((r) => setEvents(r.events))
      .catch(() => setEvents([]));
  }, [engagementId]);

  if (!events) return <Spinner label="Loading audit trail" />;

  return (
    <Card>
      <SectionHeading
        title="Audit trail"
        subtitle="Append-only. Stage transitions, gate approvals, corridor exceptions, calculation runs, external pulls and releases."
      />
      <ol className="divide-y divide-slate-100">
        {events.map((ev) => (
          <li key={ev.id} className="flex items-start gap-3 px-6 py-3">
            <span
              className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                ev.action.includes('rejected') || ev.action.includes('failed')
                  ? 'bg-rose-400'
                  : ev.action.includes('passed') || ev.action.includes('approved') || ev.action.includes('released')
                    ? 'bg-emerald-400'
                    : ev.actorKind === 'system' || ev.actorKind === 'connector'
                      ? 'bg-slate-300'
                      : 'bg-teal-400'
              }`}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800">{humaniseAction(ev.action)}</p>
              <p className="mt-0.5 text-xs text-slate-400">
                {ev.actorLabel ?? 'system'} · {dateTime(ev.createdAt)} ({relativeTime(ev.createdAt)})
              </p>
              {Object.keys(ev.detail ?? {}).length > 0 ? (
                <p className="mt-1 truncate font-mono text-[11px] text-slate-400">
                  {JSON.stringify(ev.detail)}
                </p>
              ) : null}
            </div>
          </li>
        ))}
        {events.length === 0 ? (
          <li>
            <EmptyState title="No events yet" />
          </li>
        ) : null}
      </ol>
    </Card>
  );
}
